// Query Mapper - Converts between DTO and Domain Entity
import {
  QueryEntity,
  QueryStatus,
  QueryPriority,
  QuerySource,
  UserEntity,
  QueryAttachmentEntity,
} from "../../domain/entities/QueryEntity";
import { QueryDTO, UserDTO, QueryAttachmentDTO } from "../models/QueryDTO";
import { QueryCommentMapper } from "./QueryCommentMapper";
import { QueryHistoryMapper } from "./QueryHistoryMapper";
import { parseInvoiceJson } from "../../../../features/invoices/data/mappers/InvoiceMapper";
import { parseBillJson } from "../../../../features/bills/data/api/billsApi";

export class QueryMapper {
  /**
   * Parse source_details based on the source type
   */
  static parseSourceDetails(source: string, sourceDetails: any): any {
    if (!sourceDetails) {
      return null;
    }

    // If source_details is already an object with parsed data, return as-is
    if (typeof sourceDetails === "object" && !Array.isArray(sourceDetails)) {
      // Check if invoice_json or bill_json needs parsing
      if (source === QuerySource.INVOICE && sourceDetails.invoice_json) {
        return {
          ...sourceDetails,
          invoiceJson:
            typeof sourceDetails.invoice_json === "string"
              ? parseInvoiceJson(sourceDetails.invoice_json)
              : sourceDetails.invoice_json,
        };
      }

      if (source === QuerySource.BILL && sourceDetails.bill_json) {
        return {
          ...sourceDetails,
          billJson:
            typeof sourceDetails.bill_json === "string"
              ? parseBillJson(sourceDetails.bill_json)
              : sourceDetails.bill_json,
        };
      }

      return sourceDetails;
    }

    return sourceDetails;
  }

  static toDomain(dto: QueryDTO): QueryEntity {
    return {
      queryId: dto.query_id,
      parentQueryId: dto.parent_query_id,
      raisedBy: this.userToDomain(dto.raised_by),
      assignedTo: dto.assigned_to ? this.userToDomain(dto.assigned_to) : null,
      title: dto.title,
      description: dto.description,
      status: dto.status as QueryStatus,
      priority: dto.priority as QueryPriority,
      source: dto.source as QuerySource,
      sourceId: dto.source_id,
      reviewRequired: dto.review_required,
      createdAt: new Date(dto.created_at),
      updatedAt: new Date(dto.updated_at),
      closedAt: dto.closed_at ? new Date(dto.closed_at) : null,
      subQueries: dto.sub_queries?.map((sq) => QueryMapper.toDomain(sq)),
      comments: dto.comments?.map((c) => QueryCommentMapper.toDomain(c)),
      history: dto.history?.map((h) => QueryHistoryMapper.toDomain(h)),
      sourceDetails: this.parseSourceDetails(dto.source, dto.source_details),
      attachments: dto.attachments?.map((att) => ({
        id: typeof att.id === "string" ? parseInt(att.id, 10) : att.id,
        query_id:
          typeof att.query_id === "string"
            ? parseInt(att.query_id, 10)
            : att.query_id,
        file_url: att.file_url,
        file_name: att.file_name,
        file_size:
          att.file_size !== undefined
            ? typeof att.file_size === "string"
              ? parseInt(att.file_size, 10)
              : att.file_size
            : undefined,
        mime_type: att.mime_type,
        uploaded_by:
          typeof att.uploaded_by === "string"
            ? parseInt(att.uploaded_by, 10)
            : att.uploaded_by,
        uploaded_at: att.uploaded_at,
        description: att.description,
        created_at: att.created_at,
        updated_at: att.updated_at,
        signed_url: att.signed_url,
      })),
    };
  }

  static toDTO(entity: Partial<QueryEntity>): Partial<QueryDTO> {
    const dto: Partial<QueryDTO> = {};

    if (entity.queryId !== undefined) dto.query_id = entity.queryId;
    if (entity.parentQueryId !== undefined)
      dto.parent_query_id = entity.parentQueryId;
    if (entity.title !== undefined) dto.title = entity.title;
    if (entity.description !== undefined) dto.description = entity.description;
    if (entity.status !== undefined) dto.status = entity.status;
    if (entity.priority !== undefined) dto.priority = entity.priority;
    if (entity.source !== undefined) dto.source = entity.source;
    if (entity.sourceId !== undefined) dto.source_id = entity.sourceId;
    if (entity.reviewRequired !== undefined)
      dto.review_required = entity.reviewRequired;

    return dto;
  }

  static userToDomain(dto: UserDTO): UserEntity {
    return {
      id: dto.id,
      email: dto.email,
      userName: dto.userName,
      role: dto.role,
    };
  }

  static userToDTO(entity: UserEntity): UserDTO {
    return {
      id: entity.id,
      email: entity.email,
      userName: entity.userName,
      role: entity.role,
    };
  }
}
